import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import AssetsModal from "@/components/features/ClientOnboarding/AssetsInfo/AssetsModal/AssetsModal";
import ContactsModal from "@/components/features/ClientOnboarding/Contacts/ContactsModal/ContactsModal";
import AddOrEditLocationsModal from "@/components/features/ClientOnboarding/LocationInfo/AddOrEditLocations/AddOrEditLocationsModal";
import { AssetsTab } from "@/components/features/LocationDetails/AssetsTab/AssetsTab";
import { LocationHeader } from "@/components/features/LocationDetails/LocationHeader/LocationHeader";
import { LocationStatusModal } from "@/components/features/LocationDetails/LocationStatusModals/LocationStatusModal";
import LocationTabInfo from "@/components/features/LocationDetails/LocationTabInfo/LocationTabInfo";
import { LocationOverviewTab } from "@/components/features/LocationDetails/OverviewTab/LocationOverviewTab";
import { LocationDetailSkeleton, TabContentFormSkeleton } from "@/components/ui/Skeleton";
import { LocationQueryKeys } from "@/api/locations/locations.queries";
import {
  LOCATION_DETAIL_QUERY_KEYS,
  useLocationDetailData,
} from "@/hooks/locations/useLocationDetailQueries";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { Box, Flex, Tabs, Text } from "@mantine/core";
import { TabsScrollArea } from "@/components/ui/Tab/TabsScrollArea";
import { Card } from "@onengine/component-library";
import { useDisclosure } from "@mantine/hooks";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { z } from "zod";
import styles from "./locationDetail.module.scss";
import WorkOrder, {
  type WorkOrderTabId,
} from "@/components/features/Locations/Workorders/WorkOrder";
import { LocationContactsTab } from "@/components/features/LocationDetails/ContactsTab/LocationContactsTab";

const LOCATION_TAB_VALUES = [
  "overview",
  "location-info",
  "work-orders",
  "assets",
  "invoices",
  "documents",
  "reports",
  "contacts",
] as const;

type LocationTabValue = (typeof LOCATION_TAB_VALUES)[number];

const searchParamsSchema = tableSearchSchema.extend({
  tab: z.enum(LOCATION_TAB_VALUES).optional().catch("overview"),
  workOrdersTab: z.enum(["active", "upcoming", "completed"]).optional().catch(undefined),
});

type TabContext = {
  isActive?: boolean;
  searchParams?: Record<string, unknown>;
  onWorkOrdersSubTabChange?: (subTab: WorkOrderTabId) => void;
};

type TabConfig = {
  value: LocationTabValue;
  label: string;
  component?: (
    id: string,
    overviewData?: any,
    isOverviewLoading?: boolean,
    headerData?: { siteId?: string },
    context?: TabContext,
  ) => ReactNode;
};

const TABS: TabConfig[] = [
  { value: "overview", label: "Overview" },
  {
    value: "location-info",
    label: "Location Info",
    component: (locationId, overviewData, isOverviewLoading) => (
      <LocationTabInfo
        locationData={overviewData?.locationInformation}
        isOverviewLoading={isOverviewLoading}
        clientId={overviewData?.clientInformation?.clientId}
        locationId={locationId}
      />
    ),
  },
  {
    value: "work-orders",
    label: "Work Orders",
    component: (id, _, __, ___, context) => (
      <WorkOrder
        locationId={id}
        defaultTab={
          (context?.searchParams?.workOrdersTab as WorkOrderTabId | undefined) ?? "active"
        }
        onSubTabChange={context?.onWorkOrdersSubTabChange}
      />
    ),
  },
  { value: "assets", label: "Assets", component: (id: string) => <AssetsTab locationId={id} /> },
  { value: "invoices", label: "Invoices" },
  { value: "documents", label: "Documents" },
  { value: "reports", label: "Reports" },
  {
    value: "contacts",
    label: "Contacts",
    component: (locationId, overviewData, isOverviewLoading, headerData, context) => (
      <LocationContactsTab
        locationId={locationId}
        clientId={overviewData?.clientInformation?.clientId}
        locationSiteId={headerData?.siteId}
        isLoading={isOverviewLoading}
        isActive={context?.isActive}
        searchParams={context?.searchParams}
      />
    ),
  },
];

export const Route = createFileRoute("/_authenticated/locations/$id")({
  component: LocationDetail,
  validateSearch: (search) => searchParamsSchema.parse(search),
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function LocationDetail() {
  const { id } = useParams({ from: "/_authenticated/locations/$id" });
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/_authenticated/locations/$id" });
  const activeTab = searchParams.tab ?? "overview";

  useEffect(() => {
    if (!searchParams.tab) {
      navigate({
        to: "/locations/$id",
        params: { id },
        search: { tab: "overview" },
        replace: true,
      });
    }
  }, [searchParams.tab, navigate, id]);

  const navigateToTab = useCallback(
    (tab: LocationTabValue, workOrdersSubTab?: WorkOrderTabId, replace?: boolean) => {
      navigate({
        to: "/locations/$id",
        params: { id },
        search: {
          tab,
          ...(tab === "work-orders" &&
          workOrdersSubTab &&
          workOrdersSubTab !== "active" &&
          workOrdersSubTab !== "autodispatch"
            ? { workOrdersTab: workOrdersSubTab }
            : {}),
        },
        replace,
      });
    },
    [navigate, id],
  );

  const handleTabChange = (value: string | null, workOrdersTab?: "active" | "upcoming") => {
    navigateToTab((value || "overview") as LocationTabValue, workOrdersTab);
  };

  const handleWorkOrdersSubTabChange = useCallback(
    (subTab: WorkOrderTabId) => {
      navigateToTab("work-orders", subTab, true);
    },
    [navigateToTab],
  );

  const [statusModalOpened, statusModalHandlers] = useDisclosure(false);
  const [locationEditModalOpened, locationEditModalHandlers] = useDisclosure(false);
  const [assetsModalOpened, assetsModalHandlers] = useDisclosure(false);
  const [contactsModalOpened, contactsModalHandlers] = useDisclosure(false);
  // const [, setContacts] = useState<Contact[]>([]);

  const queryClient = useQueryClient();
  const {
    headerData,
    overviewData,
    isLoading,
    isOverviewLoading,
    hasError,
    headerError,
    overviewError,
  } = useLocationDetailData(id);

  const presetLocationsManaged = useMemo(() => {
    if (!headerData?.address) return [];
    return [
      {
        id: headerData.locationId,
        siteId: headerData.siteId,
        address: {
          address1: headerData.address.address1 ?? "",
          address2: headerData.address.address2 ?? null,
          city: headerData.address.city ?? "",
          state: headerData.address.state ?? "",
          zipcode: headerData.address.zipcode ?? null,
        },
      },
    ];
  }, [headerData]);

  const siteId = headerData?.siteId;
  const breadcrumbItems = useMemo(
    () => [
      {
        title: "Locations",
        href: "/locations",
        icon: getRouteIcon(MODULE_KEYS_MAP.locations, 16),
      },
      {
        title: siteId || "Loading...",
        href: `/locations/${id}`,
      },
    ],
    [siteId, id],
  );
  useBreadcrumb(breadcrumbItems, { listView: "List View" });

  if (isLoading) {
    return <LocationDetailSkeleton />;
  }

  if (hasError) {
    return (
      // I was not able to get into this screen
      // Temporary comment: minHeight "100vh" causes extra scroll — AppShell.Main already offsets the header, so 100vh overflows the scrollable area. Change to "100%" to fill parent without overflow.
      <Flex
        direction="column"
        style={{
          margin: "calc(var(--mantine-spacing-md) * -1)",
          backgroundColor: "var(--neutral-500)",
          minHeight: "100vh",
        }}
      >
        <Box className={styles.loadingContainer}>
          <Text size="lg" c="red" fw={600}>
            Error loading location details
          </Text>
          <Text size="sm" c="dimmed" mt="sm">
            {headerError?.message || overviewError?.message || "An unexpected error occurred"}
          </Text>
        </Box>
      </Flex>
    );
  }

  if (!headerData) return null;

  return (
    <>
      {/* Temporary comment: minHeight "100vh" causes extra scroll — AppShell.Main already offsets the header, so 100vh overflows the scrollable area. Change to "100%" to fill parent without overflow. */}
      <Flex
        direction="column"
        style={{
          margin: "calc(var(--mantine-spacing-md) * -1)",
          backgroundColor: "var(--neutral-500)",
          minHeight: "100vh",
        }}
      >
        <LocationHeader
          data={headerData}
          onToggleStatus={statusModalHandlers.open}
          onEditLocation={locationEditModalHandlers.open}
          onAddNewClick={(value) => {
            if (value === "add-asset") assetsModalHandlers.open();
            if (value === "add-contact") contactsModalHandlers.open();
          }}
        />

        <Box className={styles.tabsContainer}>
          <Tabs value={activeTab} onChange={handleTabChange} className={styles.tabs}>
            <TabsScrollArea>
              <Tabs.List className={styles.tabsList}>
                {TABS.map((tab) => (
                  <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
                    {tab.label}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </TabsScrollArea>

            {TABS.map((tab) => (
              <Tabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
                {activeTab === tab.value && tab.value === "overview" ? (
                  overviewData && !isOverviewLoading ? (
                    <LocationOverviewTab
                      locationId={id}
                      data={overviewData}
                      onTabChange={handleTabChange}
                    />
                  ) : (
                    <TabContentFormSkeleton />
                  )
                ) : activeTab === tab.value && tab.component ? (
                  <>
                    {tab.component(id, overviewData, isOverviewLoading, headerData, {
                      isActive: activeTab === tab.value,
                      searchParams,
                      onWorkOrdersSubTabChange: handleWorkOrdersSubTabChange,
                    })}
                  </>
                ) : (
                  activeTab === tab.value && (
                    <Card>
                      <Text>{tab.label}</Text>
                    </Card>
                  )
                )}
              </Tabs.Panel>
            ))}
          </Tabs>
        </Box>
      </Flex>

      {/* Modals */}

      <LocationStatusModal
        openModal={statusModalOpened}
        closeModal={statusModalHandlers.close}
        locationId={headerData.locationId}
        siteId={headerData.siteId}
        currentStatus={headerData.operableStatus?.toLowerCase() as "operable" | "inoperable"}
        queryKeysToInvalidate={[
          LOCATION_DETAIL_QUERY_KEYS.HEADER,
          LocationQueryKeys.list,
          LocationQueryKeys.quickView,
        ]}
      />

      <AddOrEditLocationsModal
        openModal={locationEditModalOpened}
        setOpenModal={locationEditModalHandlers.close}
        editingLocationId={headerData.locationId}
        clientId={overviewData?.clientInformation?.clientId ?? null}
        isLocationModule
        queryKeysToInvalidate={[
          LOCATION_DETAIL_QUERY_KEYS.HEADER,
          LOCATION_DETAIL_QUERY_KEYS.OVERVIEW,
        ]}
      />

      <AssetsModal
        openModal={assetsModalOpened}
        setOpenModal={(open) => {
          if (!open) assetsModalHandlers.close();
        }}
        clientId={overviewData?.clientInformation?.clientId ?? null}
        locationId={id}
      />

      <ContactsModal
        openModal={contactsModalOpened}
        setOpenModal={(open) => {
          if (!open) contactsModalHandlers.close();
        }}
        setAssets={() => {}}
        editingAsset={null}
        clientId={overviewData?.clientInformation?.clientId}
        disableLocationsManaged={true}
        presetLocationsManaged={presetLocationsManaged}
        onContactsCreated={() => {
          queryClient.invalidateQueries({ queryKey: [LocationQueryKeys.contacts, id] });
          queryClient.invalidateQueries({ queryKey: [LOCATION_DETAIL_QUERY_KEYS.HEADER] });
        }}
      />
    </>
  );
}
